/**
 * The game HUD.
 *
 * Plain DOM, built once, in the shape of `ui/overlay.js`: a scoped stylesheet
 * injected at construction, one root element appended to the body, and updates
 * split between an event-driven pass (`refresh`, on state change) and a
 * per-frame pass (things that move: the minimap, floating damage, cooldown
 * sweeps, the cast bar).
 *
 * It reads the game through a small interface rather than reaching into it, so
 * the panels do not care how targeting or combat are implemented:
 *   state, targetInfo(), blips(), playerFacing(), project(), castProgress(),
 *   skillCooldown(), useHotbar(), and the handful of actions the modals call.
 */

import { HUD_CSS } from "./style.js";
import { openModal, MODAL_TITLES } from "./modals.js";
import { SKILLS, ITEMS, ACTION_ICONS } from "../../game/worldData.js";
import { audio } from "../../game/audio.js";

const CHANNELS = ["all", "system", "damage", "trade", "party", "clan"];
/** Pixels per metre on the minimap. */
const RADAR_SCALE = 0.55;
const RADAR_LIMIT = 62;
const FLOAT_LIFETIME = 1200;

export class Hud {
    /** @param {import("../../game/game.js").Game} game */
    constructor(game) {
        this.game = game;
        this.state = game.state;

        const style = document.createElement("style");
        style.textContent = HUD_CSS;
        document.head.appendChild(style);

        const root = document.createElement("div");
        root.id = "hud";
        document.body.appendChild(root);
        this.root = root;

        this.chatChannel = "all";
        /** @type {string|null} */
        this.modal = null;
        this.modalEl = null;

        this._floatNodes = new Map();
        this._mapNodes = [];
        this._acc = 0;

        this._buildStatus();
        this._buildTarget();
        this._buildCastBar();
        this._buildHotbar();
        this._buildDock();
        this._buildMinimap();
        this._buildChat();
        this._buildPlateLayer();
        this._buildFloatLayer();
        this._buildDeathVeil();

        this._unsubscribe = this.state.subscribe(() => this.refresh());
        this.refresh();
    }

    // =====================================================  construction
    _panel(id, cls = "") {
        const el = document.createElement("div");
        el.id = id;
        el.className = `panel ${cls}`.trim();
        this.root.appendChild(el);
        return el;
    }

    _buildStatus() {
        const el = this._panel("hud-status");
        el.innerHTML = `
          <div class="top">
            <div class="lvl">Lv 1</div>
            <div class="who"><b>Hero</b><i>Human Knight</i></div>
            <div class="purse mono">SP <span data-sp>0</span><br>Adena <span data-adena>0</span></div>
          </div>
          <div class="bars">
            <div>
              <div class="barlabel"><span style="color:#fbbf24">CP</span><span class="mono" data-cp></span></div>
              <div class="track" style="height:9px"><div class="fill cp" data-cpfill></div></div>
            </div>
            <div>
              <div class="barlabel"><span style="color:#fb7185">HP</span><span class="mono" data-hp></span></div>
              <div class="track" style="height:11px"><div class="fill hp" data-hpfill></div></div>
            </div>
            <div>
              <div class="barlabel"><span style="color:#38bdf8">MP</span><span class="mono" data-mp></span></div>
              <div class="track" style="height:9px"><div class="fill mp" data-mpfill></div></div>
            </div>
            <div>
              <div class="barlabel"><span style="color:#34d399">EXP</span><span class="mono" data-xp></span></div>
              <div class="track" style="height:6px"><div class="fill xp" data-xpfill></div></div>
            </div>
          </div>
          <div class="buffs" data-buffs></div>`;
        this.status = el;
    }

    _buildTarget() {
        const el = this._panel("hud-target");
        el.style.display = "none";
        this.targetPanel = el;
    }

    _buildCastBar() {
        const el = this._panel("hud-cast");
        el.innerHTML = `
          <div class="name" data-castname></div>
          <div class="track" style="height:9px"><div class="fill cast" data-castfill></div></div>`;
        this.castPanel = el;
    }

    _buildHotbar() {
        const el = this._panel("hud-hotbar");
        const slots = document.createElement("div");
        slots.className = "slots";
        el.appendChild(slots);

        this.slotNodes = [];
        for (let i = 0; i < 12; i++) {
            const b = document.createElement("div");
            b.className = "slot";
            b.innerHTML =
                `<div class="key">F${i + 1}</div><div class="icon"></div><div class="qty"></div>` +
                `<div class="cd" style="display:none"></div>`;
            b.addEventListener("click", () => {
                audio.playUiClick();
                this.game.useHotbar(i);
            });
            slots.appendChild(b);
            this.slotNodes.push(b);
        }
        this.hotbar = el;
    }

    _buildDock() {
        const el = this._panel("hud-dock");
        const buttons = [
            ["character", "🧍", "CHAR"],
            ["inventory", "🎒", "BAG"],
            ["skills", "⚡", "SKILL"],
            ["quests", "📜", "QUEST"],
            ["classes", "🛡️", "CLASS"],
            ["sound", "🔊", "SOUND"],
            ["system", "⚙️", "MENU"],
        ];
        for (const [name, glyph, label] of buttons) {
            const b = document.createElement("button");
            b.innerHTML = `<span class="g">${glyph}</span><span class="t">${label}</span>`;
            b.title = MODAL_TITLES[name] ?? label;
            b.addEventListener("click", () => {
                audio.playUiClick();
                if (name === "sound") {
                    const muted = audio.toggleMute();
                    b.querySelector(".g").textContent = muted ? "🔇" : "🔊";
                    this.state.system(`Sound ${muted ? "muted" : "on"}.`);
                } else {
                    this.toggleModal(name);
                }
            });
            el.appendChild(b);
        }
        this.dock = el;
    }

    _buildMinimap() {
        const el = this._panel("hud-map");
        el.innerHTML = `
          <div class="ring"></div><div class="cross h"></div><div class="cross v"></div>
          <div class="compass n">N</div><div class="compass s">S</div>
          <div class="compass w">W</div><div class="compass e">E</div>
          <div class="blip me" style="left:50%;top:50%"></div>`;
        this.map = el;
    }

    _buildChat() {
        const el = this._panel("hud-chat");
        const tabs = document.createElement("div");
        tabs.className = "tabs";
        this.tabNodes = {};
        for (const ch of CHANNELS) {
            const b = document.createElement("button");
            b.textContent = ch;
            b.addEventListener("click", () => {
                this.chatChannel = ch;
                this.refreshChat();
            });
            tabs.appendChild(b);
            this.tabNodes[ch] = b;
        }
        el.appendChild(tabs);

        const log = document.createElement("div");
        log.className = "log";
        el.appendChild(log);
        this.chatLog = log;

        const form = document.createElement("form");
        form.innerHTML =
            `<input type="text" placeholder="Press Enter to chat…" maxlength="120">` +
            `<button class="send" type="submit">➤</button>`;
        form.addEventListener("submit", (e) => {
            e.preventDefault();
            const input = form.querySelector("input");
            const text = input.value.trim();
            if (text) this.state.addChat("Hero", "all", text);
            input.value = "";
            input.blur();
        });
        el.appendChild(form);
        this.chatInput = form.querySelector("input");
        this.chat = el;
    }

    _buildPlateLayer() {
        const el = document.createElement("div");
        el.id = "hud-plates";
        this.root.appendChild(el);
        this.plateLayer = el;
        /** @type {Map<string, HTMLElement>} */
        this._plateNodes = new Map();
    }

    _buildFloatLayer() {
        const el = document.createElement("div");
        el.id = "hud-float";
        this.root.appendChild(el);
        this.floatLayer = el;
    }

    _buildDeathVeil() {
        const el = document.createElement("div");
        el.id = "hud-death";
        el.innerHTML = `
          <h1>You have died</h1>
          <p>The snow takes everyone eventually.</p>
          <button class="btn">Return to the village</button>`;
        el.querySelector("button").addEventListener("click", () => {
            audio.playUiClick();
            this.game.revive();
        });
        this.root.appendChild(el);
        this.deathVeil = el;
    }

    // ==========================================================  updates
    /** Event-driven repaint: everything that only changes when the state does. */
    refresh() {
        this.refreshStatus();
        this.refreshTarget();
        this.refreshHotbar();
        this.refreshChat();
        this.deathVeil.classList.toggle("show", this.state.isDead);
        if (this.modal) this.renderModal();
    }

    refreshStatus() {
        const s = this.state.stats;
        const el = this.status;
        const q = (sel) => el.querySelector(sel);

        _txt(q(".lvl"), `Lv ${s.level}`);
        _txt(q(".who b"), "Hero");
        _txt(q(".who i"), this.state.playerClass);
        _txt(q("[data-sp]"), Math.floor(s.sp).toLocaleString());
        _txt(q("[data-adena]"), Math.floor(s.adena).toLocaleString());

        setBar(q("[data-cp]"), q("[data-cpfill]"), s.cp, s.maxCp);
        setBar(q("[data-hp]"), q("[data-hpfill]"), s.hp, s.maxHp);
        setBar(q("[data-mp]"), q("[data-mpfill]"), s.mp, s.maxMp);

        const xpPct = clamp01(s.exp / s.maxExp) * 100;
        _txt(q("[data-xp]"), `${xpPct.toFixed(1)}%`);
        q("[data-xpfill]").style.width = `${xpPct}%`;

        const buffs = q("[data-buffs]");
        const now = performance.now();
        let html = "";
        if (s.soulshotActive) html += `<span class="chip">✨ SS Active</span>`;
        for (const b of this.state.buffs) {
            const left = Math.max(0, Math.ceil((b.until - now) / 1000));
            html += `<span class="chip amber">${b.icon} ${b.name} ${left}s</span>`;
        }
        _html(buffs, html);
    }

    refreshTarget() {
        const info = this.game.targetInfo();
        const el = this.targetPanel;
        if (!info) {
            el.style.display = "none";
            return;
        }
        el.style.display = "block";
        el.classList.toggle("npc", info.kind === "npc");

        if (info.kind === "npc") {
            _html(el, `
              <div class="row">
                <span class="badge npc">NPC</span>
                <span class="name">${escapeHtml(info.name)}</span>
              </div>
              <div style="font-size:11px;color:#7dd3fc">${escapeHtml(info.title ?? "")}</div>
              <div class="hint">Click again to talk.</div>`);
            return;
        }

        const pct = clamp01(info.hp / info.maxHp) * 100;
        const badge = info.type === "boss" ? "boss" : info.type;
        const label = info.type === "boss" ? "Raid Boss" : info.type;
        _html(el, `
          <div class="row">
            <span class="badge passive" style="background:#334155;color:#fcd34d">Lv ${info.level}</span>
            <span class="name">${escapeHtml(info.name)}</span>
            <span class="badge ${badge}">${label}</span>
          </div>
          <div class="barlabel"><span></span>
            <span class="mono" style="color:#fb7185">${Math.ceil(info.hp)} / ${info.maxHp} (${pct.toFixed(0)}%)</span>
          </div>
          <div class="track" style="height:12px"><div class="fill hp" style="width:${pct}%"></div></div>`);
    }

    refreshHotbar() {
        const st = this.state;
        for (let i = 0; i < 12; i++) {
            const slot = st.hotbar[i];
            const node = this.slotNodes[i];
            const icon = node.querySelector(".icon");
            const qty = node.querySelector(".qty");

            let glyph = "";
            let text = "";
            let active = false;
            let title = "";

            if (slot.type === "skill") {
                const skill = SKILLS[slot.targetId];
                if (skill) {
                    glyph = skill.icon;
                    title = `${skill.name} — ${skill.description}`;
                    if (slot.targetId === "soulshot_toggle") active = st.stats.soulshotActive;
                }
            } else if (slot.type === "item") {
                const item = ITEMS[slot.targetId];
                if (item) {
                    glyph = item.icon;
                    title = item.name;
                    const stack = st.findStack(slot.targetId);
                    text = stack ? String(stack.quantity) : "0";
                }
            } else if (slot.type === "action") {
                glyph = ACTION_ICONS[slot.targetId] ?? "❓";
                title = slot.targetId;
                if (slot.targetId === "sit") active = st.isSitting;
                if (slot.targetId === "surf") active = this.game.surfLocked;
            }

            _txt(icon, glyph);
            _txt(qty, text);
            node.title = title;
            node.classList.toggle("on", active);
        }
    }

    refreshChat() {
        for (const ch of CHANNELS) {
            this.tabNodes[ch].classList.toggle("on", ch === this.chatChannel);
        }
        const msgs = this.state.chatMessages.filter(
            (m) => this.chatChannel === "all" || m.channel === this.chatChannel
        );
        const html = msgs
            .map(
                (m) =>
                    `<div class="line ${m.channel}"><span class="ts">${m.timestamp}</span>` +
                    `<b>${escapeHtml(m.sender)}:</b> ${escapeHtml(m.text)}</div>`
            )
            .join("");
        if (_html(this.chatLog, html)) {
            this.chatLog.scrollTop = this.chatLog.scrollHeight;
        }
    }

    /**
     * Per-frame pass. Only the things that move.
     * @param {number} dtMs
     */
    update(dtMs) {
        this._acc += dtMs;
        this.updatePlates();
        this.updateFloatingText();
        this.updateCastBar();
        this.updateCooldowns();
        if (this._acc >= 50) {
            this._acc = 0;
            this.updateMinimap();
            if (this.state.buffs.length) this.refreshStatus();
        }
    }

    /**
     * Names over heads.
     *
     * Nodes are keyed by actor id and reused; a mob that walks out of range
     * takes its plate with it rather than leaving one hanging in the fog.
     */
    updatePlates() {
        const plates = this.game.plates();
        const live = new Set();

        for (const p of plates) {
            live.add(p.id);
            let node = this._plateNodes.get(p.id);
            if (!node) {
                node = document.createElement("div");
                node.innerHTML =
                    `<div class="nm"></div><div class="lv"></div>` +
                    `<div class="hp"><i></i></div>`;
                this.plateLayer.appendChild(node);
                this._plateNodes.set(p.id, node);
            }
            const screen = this.game.project(p.x, p.y, p.z);
            if (!screen) {
                node.style.display = "none";
                continue;
            }
            node.style.display = "block";
            node.style.left = `${screen.x}px`;
            node.style.top = `${screen.y}px`;
            const cls = `plate ${p.kind}${p.selected ? " sel" : ""}`;
            if (node.className !== cls) node.className = cls;
            _txt(node.firstChild, p.name);
            _txt(node.children[1], p.sub);

            const bar = node.children[2];
            const hurt = p.hpPct !== null && p.hpPct < 1;
            bar.style.display = hurt ? "block" : "none";
            if (hurt) bar.firstChild.style.width = `${Math.max(0, p.hpPct) * 100}%`;
        }

        for (const [id, node] of this._plateNodes) {
            if (!live.has(id)) {
                node.remove();
                this._plateNodes.delete(id);
            }
        }
    }

    updateFloatingText() {
        const now = performance.now();
        const live = new Set();

        for (const ft of this.state.floatingTexts) {
            const age = (now - ft.born) / FLOAT_LIFETIME;
            if (age >= 1) continue;

            let node = this._floatNodes.get(ft.id);
            if (!node) {
                node = document.createElement("div");
                node.textContent = ft.text;
                node.style.color = ft.color;
                if (ft.big) node.className = "big";
                this.floatLayer.appendChild(node);
                this._floatNodes.set(ft.id, node);
            }
            const p = this.game.project(ft.x, ft.y, ft.z);
            if (!p) {
                node.style.display = "none";
                live.add(ft.id);
                continue;
            }
            node.style.display = "block";
            node.style.left = `${p.x}px`;
            node.style.top = `${p.y - age * 42}px`;
            node.style.opacity = String(1 - age * age);
            live.add(ft.id);
        }

        for (const [id, node] of this._floatNodes) {
            if (!live.has(id)) {
                node.remove();
                this._floatNodes.delete(id);
            }
        }
    }

    updateCastBar() {
        const cast = this.game.castProgress();
        if (!cast) {
            this.castPanel.classList.remove("show");
            return;
        }
        this.castPanel.classList.add("show");
        _txt(this.castPanel.querySelector("[data-castname]"), cast.name);
        this.castPanel.querySelector("[data-castfill]").style.width = `${clamp01(cast.t) * 100}%`;
    }

    updateCooldowns() {
        for (let i = 0; i < 12; i++) {
            const slot = this.state.hotbar[i];
            const node = this.slotNodes[i];
            const cd = node.querySelector(".cd");
            if (slot.type !== "skill") {
                if (cd.style.display !== "none") cd.style.display = "none";
                continue;
            }
            const left = this.game.skillCooldown(slot.targetId);
            if (left <= 0) {
                if (cd.style.display !== "none") cd.style.display = "none";
            } else {
                cd.style.display = "flex";
                _txt(cd, left >= 10 ? String(Math.ceil(left)) : left.toFixed(1));
            }
        }
    }

    updateMinimap() {
        const blips = this.game.blips();
        const px = this.game.playerPos;

        // Reuse nodes: the blip count is stable and churning DOM at 20 Hz for a
        // dozen dots is the sort of thing that shows up in a frame graph.
        while (this._mapNodes.length < blips.length) {
            const n = document.createElement("div");
            n.className = "blip mob";
            this.map.appendChild(n);
            this._mapNodes.push(n);
        }

        for (let i = 0; i < this._mapNodes.length; i++) {
            const node = this._mapNodes[i];
            const b = blips[i];
            if (!b) {
                node.style.display = "none";
                continue;
            }
            const rx = (b.x - px.x) * RADAR_SCALE;
            const rz = (b.z - px.z) * RADAR_SCALE;
            if (Math.hypot(rx, rz) > RADAR_LIMIT) {
                node.style.display = "none";
                continue;
            }
            node.style.display = "block";
            // North is -Z, and the map is drawn north-up, so Z grows downward.
            node.style.left = `calc(50% + ${rx}px)`;
            node.style.top = `calc(50% + ${rz}px)`;
            const cls = `blip ${b.kind}${b.selected ? " sel" : ""}`;
            if (node.className !== cls) node.className = cls;
        }
    }

    // ===========================================================  modals
    toggleModal(name) {
        if (this.modal === name) this.closeModal();
        else this.openModal(name);
    }

    openModal(name) {
        this.closeModal();
        this.modal = name;
        this.modalEl = openModal(name, this);
        if (this.modalEl) this.root.appendChild(this.modalEl);
        else this.modal = null;
    }

    renderModal() {
        if (!this.modal) return;
        const next = openModal(this.modal, this);
        if (!next) return;
        this.modalEl?.remove();
        this.modalEl = next;
        this.root.appendChild(next);
    }

    closeModal() {
        this.modalEl?.remove();
        this.modalEl = null;
        this.modal = null;
    }

    closeAll() {
        this.closeModal();
        this.chatInput.blur();
    }

    get modalOpen() {
        return this.modal !== null;
    }

    focusChat() {
        if (document.activeElement === this.chatInput) this.chatInput.blur();
        else this.chatInput.focus();
    }

    dispose() {
        this._unsubscribe?.();
        this.root.remove();
    }
}

// ------------------------------------------------------------- helpers

/** Write only when the string actually changed. */
export function _txt(el, s) {
    if (el && el.textContent !== s) el.textContent = s;
    return el;
}

function _html(el, s) {
    if (el && el.innerHTML !== s) {
        el.innerHTML = s;
        return true;
    }
    return false;
}

function setBar(label, fill, value, max) {
    const v = Math.max(0, Math.ceil(value));
    _txt(label, `${v} / ${Math.ceil(max)}`);
    fill.style.width = `${clamp01(value / max) * 100}%`;
}

function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ));
}
